import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { IPaymentPayload } from "../..";
import { ExclamationIconSvgPPP, GreenTickIconSvgPPP } from "../svgImages";
import { debounce } from "lodash";
import { verifyPanNumber } from "../../../../api/verification.api";
import SnackbarContext from "../../../../context/snackbar.context";
import "./style.scss";

interface IBillingDetailsCard{
  internationalType: boolean;
  fieldsDisabled: boolean;
  paymentPayload: IPaymentPayload;
  setPaymentPayload: Dispatch<SetStateAction<IPaymentPayload>>;
  paymentGoAhead: boolean;
  setPaymentGoAhead: Dispatch<SetStateAction<boolean>>;
  gstError: boolean;
  setGstErrorsObj: Dispatch<SetStateAction<any>>;
}

interface IBillingDetailsError{
  firstName: boolean;
  phoneNumber: boolean;
  email: boolean;
  pan: boolean;
  address: boolean;
  postalCode: boolean
}

const BillingDetailsCard: React.FC<IBillingDetailsCard> = (props) => {
  const snackbarCtx = useContext(SnackbarContext);
  const { internationalType, paymentPayload, fieldsDisabled, setPaymentPayload, paymentGoAhead, setPaymentGoAhead, gstError, setGstErrorsObj } = props;
  const [errorsObj, setErrorsObj] = useState<IBillingDetailsError>({ firstName: false, phoneNumber: false, email: false, pan: false, address: false, postalCode: false });
  const [loading, setLoading] = useState<boolean>(false);

  const handleNRICheckbox = () => {
    setPaymentGoAhead(false);
    const { pan, gstNumber, gstName, ...restKeys } = paymentPayload;
    setPaymentPayload({ ...restKeys, isNri: !paymentPayload?.isNri });
    setGstErrorsObj({ gstNumber: false, gstName: false });
    setErrorsObj((prevValues: IBillingDetailsError) => ({ ...prevValues, pan: false }));
  };

  const debouncedFn = debounce(async (value) => {
    const response = await verifyPanNumber(value);
    if (response?.status_code === 200) {
      setPaymentPayload((prevValues: IPaymentPayload) => ({ ...prevValues, firstName: response?.full_name || prevValues?.firstName, pan: value?.toUpperCase() }));
      setErrorsObj((prevValues: IBillingDetailsError) => ({ ...prevValues, firstName: false, pan: false }));
      snackbarCtx.toggleSnackbar({
        message: "Customer name updated against provided PAN.",
        type: "SUCCESS",
        visible: true,
      });
    } else if (response?.status_code === 422) {
      setPaymentPayload((prevValues: IPaymentPayload) => ({ ...prevValues, pan: value?.toUpperCase() }));
      setErrorsObj((prevValues: IBillingDetailsError) => ({ ...prevValues, pan: true }));
      setPaymentGoAhead(false);
    } else setPaymentPayload((prevValues: IPaymentPayload) => ({ ...prevValues, pan: value?.toUpperCase() }));
    setLoading(false);
  }, 500);

  const handleBillingDetails = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const regexMap: { [key: string]: RegExp } = {
      firstName: /^(?=.*[a-zA-Z])[a-zA-Z\s().\-0-9\/&]+$/,
      phoneNumber: /^\d{10}$/,
      email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9]{2,})+$/,
      pan: /^[a-zA-Z]{5}\d{4}[a-zA-Z]$/,
      address: /^.+$/
    };

    if (regexMap[name]) {
      if (regexMap[name].test(value)) { 
        setPaymentPayload((prevValues: IPaymentPayload) => ({ ...prevValues, [name]: name === "pan" ? value.toUpperCase() : value }));
        setErrorsObj({ ...errorsObj, [name]: false });
        if (name === "pan") {
          setLoading(true);
          debouncedFn(value);
        }
      } else {
        setPaymentPayload((prevValues: IPaymentPayload) => ({ ...prevValues, [name]: name === "pan" ? value.toUpperCase() : value }));
        setErrorsObj({ ...errorsObj, [name]: true });
        setPaymentGoAhead(false);
        if (name === "pan") {
          setLoading(false);
          debouncedFn.cancel();
        }
      }
    } else {
      setPaymentPayload((prevValues: IPaymentPayload) => ({ ...prevValues, [name]: value }));
      setErrorsObj({ ...errorsObj, [name]: false });
    }
  }, [paymentPayload, errorsObj])

  const handleGSTDetailsCheck = () => {
    if (!!paymentPayload?.gstNumber || !!paymentPayload?.gstName) {
      if (/^\d{2}[a-zA-Z]{5}\d{4}[a-zA-Z]\d[Zz][a-zA-Z\d]$/.test(paymentPayload?.gstNumber || '') && /^(?=.*[a-zA-Z])[a-zA-Z\s().\-0-9\/&]+$/.test(paymentPayload?.gstName || '')) return true;
      else return false;
    } else return true;
  };

  const handleAgreeCheckbox = () => {
    if (
      !gstError && handleGSTDetailsCheck() &&
      !!paymentPayload?.firstName && !!paymentPayload?.phoneNumber &&
      !!paymentPayload?.email && (paymentPayload?.isNri ? true : !!paymentPayload?.pan) &&
      !!paymentPayload?.address && !paymentGoAhead && !Object.values(errorsObj).some(error => error)
    ) setPaymentGoAhead(true);
    else setPaymentGoAhead(false);
  };

  useEffect(() => { if(!!paymentPayload.gstNumber && !gstError) setErrorsObj((prevValues: IBillingDetailsError) => ({ ...prevValues, firstName: false, pan: false })); }, [paymentPayload?.gstNumber])

  return (
    <div className="billing-details-wrapper">
      <div className="billing-details-header">
        <span>Billing Details</span>
      </div>
      {!internationalType && <label className="nri-checkbox" onChange={handleNRICheckbox}>
        <input type="checkbox" checked={paymentPayload?.isNri} disabled={fieldsDisabled || loading} />
        <span className="tick-mark"></span>
        <span className={`label-text ${paymentPayload?.isNri ? "active" : ""}`}>I'm an NRI</span>
      </label>}
      <div className="billing-details-form-wrapper">
        <div className="relative-wrapper">
          <input type="text" name="firstName" id="firstName" placeholder="Full Name" onChange={handleBillingDetails} value={paymentPayload?.firstName} disabled={fieldsDisabled || (!!paymentPayload?.pan && !errorsObj.pan)} />
          {errorsObj?.firstName && <span className="abs-wrapper" title="Invalid Name"><ExclamationIconSvgPPP/></span>}
        </div>
        <div className="input-wrapper">
          <div className="relative-wrapper">
            <input type="text" name="phoneNumber" id="phoneNumber" placeholder="Phone" onChange={handleBillingDetails} value={paymentPayload?.phoneNumber} disabled={fieldsDisabled} />
            {errorsObj?.phoneNumber && <span className="abs-wrapper" title="Invalid Number"><ExclamationIconSvgPPP/></span>}
          </div>
          <div className="relative-wrapper">
            <input type="text" name="email" id="email" placeholder="Email" onChange={handleBillingDetails} value={paymentPayload?.email} disabled={fieldsDisabled} />
            {errorsObj?.email && <span className="abs-wrapper" title="Invalid Email"><ExclamationIconSvgPPP/></span>}
          </div>
        </div>
        {!paymentPayload?.isNri && <div className="relative-wrapper">
          <input type="text" name="pan" id="pan" placeholder="PAN No." onChange={handleBillingDetails} value={paymentPayload?.pan} disabled={fieldsDisabled || loading || (!!paymentPayload?.gstNumber && !gstError)} />
          {errorsObj?.pan && !loading && <span className="abs-wrapper" title="Invalid PAN"><ExclamationIconSvgPPP /></span>}
          {loading && <span className="abs-wrapper loader" title="loading"></span>}
          {!errorsObj?.pan && !!paymentPayload?.pan && !loading && <span className="abs-wrapper" title="Valid PAN"><GreenTickIconSvgPPP /></span>}
        </div>}
        <div className="input-wrapper">
          <div className="relative-wrapper">
            <input type="text" name="address" id="address" placeholder="Address" onChange={handleBillingDetails} value={paymentPayload?.address} disabled={fieldsDisabled} />
            {errorsObj?.address && <span className="abs-wrapper" title="Invalid Address"><ExclamationIconSvgPPP/></span>}
          </div>
          <div className="relative-wrapper">
            <input type="text" name="postalCode" id="postalCode" placeholder="Postal Code" onChange={handleBillingDetails} value={paymentPayload?.postalCode} disabled={fieldsDisabled} />
          </div> 
        </div>
      </div>
      <div className="agree-wrapper">
        <label className="agree-checkbox" onChange={handleAgreeCheckbox}>
          <input type="checkbox" checked={paymentGoAhead} disabled={fieldsDisabled || loading} />
          <span className="tick-mark"></span>
          <span className={`label-text ${paymentGoAhead ? "active" : ""}`}>By clicking Agree and Continue, I hereby:</span>
        </label>
        <p>
          Agree and consent to the Privacy Policy, Cancellation and Rescheduling Policy, Passenger Cruise Ticket
          Contract and all other Terms and Conditions as appear hereinabove and on website www.cordeliacruises.com.
        </p>
        {!paymentPayload?.isNri && internationalType && <p>Agree that the tax regime is selected as per my requirements.</p>}
        {!paymentPayload?.isNri && internationalType && !paymentPayload?.tcsSlab && <p>Agree that TDS is selected as per my requirements.</p>}
        {internationalType && paymentPayload?.tcsSlab &&
          <>
          <p>Agree that TCS is selected as per my requirements.</p>
          <p>In the financial year, if the Tour expenses are less than 7 lacs then 5% TCS will be levied</p>
          <p>In the financial year, if the Tour expenses are more than 7 lacs then 20% TCS will be levied</p>
          <p>TCS will apply to the total cost (i.e.((Cabin fare + Service Charge & Levies + Fuel charge) * GST) * TCS)</p>
          </>}
      </div>
    </div>
  );
};

export default BillingDetailsCard;
